import { createRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { components } from '@trackercop/contracts';
import { rootRoute } from './__root';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { DataSharingMap } from '@/components/data-sharing-map';
import {
  buildDataSharingGraph,
  type DataSharingDestinationNode,
  type DataSharingScriptNode,
} from '@/lib/data-sharing-graph';
import { PayloadCategoryChip } from '@/components/category-chip';
import { CONSENT_MODES } from '@/lib/constants';
import { Select } from '@/components/ui/select';

type ConsentMode = components['schemas']['ConsentMode'];
type PayloadRecord = components['schemas']['PayloadRecord'];

interface DataSharingSearch {
  mode?: ConsentMode | 'all';
  sensitive?: boolean;
}

export const crawlDataSharingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crawls/$crawlId/data-sharing',
  component: DataSharingPage,
  validateSearch: (search: Record<string, unknown>): DataSharingSearch => ({
    mode: search.mode as ConsentMode | 'all' | undefined,
    sensitive: search.sensitive === true || search.sensitive === 'true',
  }),
});

// API caps pageSize at 200 (see openapi.yaml). Pagination loop walks up to
// MAX_PAYLOAD_PAGES * PAYLOADS_PAGE_SIZE records so the Sankey reflects a
// full crawl; the truncation banner only fires above that ceiling.
const PAYLOADS_PAGE_SIZE = 200;
const MAX_PAYLOAD_PAGES = 10;

function DataSharingPage() {
  const { crawlId } = crawlDataSharingRoute.useParams();
  const search = crawlDataSharingRoute.useSearch();
  const navigate = crawlDataSharingRoute.useNavigate();
  const consentMode: ConsentMode | 'all' = search.mode ?? 'all';
  const sensitiveOnly = search.sensitive === true;

  const [selectedDest, setSelectedDest] = useState<DataSharingDestinationNode | null>(null);
  const [selectedScript, setSelectedScript] = useState<DataSharingScriptNode | null>(null);

  const payloadsQuery = useQuery({
    queryKey: queryKeys.crawls.payloadsAll(crawlId, PAYLOADS_PAGE_SIZE),
    queryFn: async () => {
      const items: PayloadRecord[] = [];
      let total = 0;
      for (let page = 1; page <= MAX_PAYLOAD_PAGES; page++) {
        const { data, error } = await apiClient.GET('/crawls/{crawlId}/payloads', {
          params: {
            path: { crawlId },
            query: { page, pageSize: PAYLOADS_PAGE_SIZE },
          },
        });
        if (error) throw error;
        total = data.total;
        items.push(...data.items);
        if (items.length >= total || data.items.length < PAYLOADS_PAGE_SIZE) break;
      }
      return { items, total };
    },
  });

  const records: PayloadRecord[] = payloadsQuery.data?.items ?? [];
  const total = payloadsQuery.data?.total ?? 0;
  const graph = useMemo(
    () => buildDataSharingGraph(records, { consentMode, sensitiveOnly }),
    [records, consentMode, sensitiveOnly],
  );

  function setSearch(next: Partial<DataSharingSearch>) {
    navigate({
      search: (prev) => ({ ...prev, ...next }),
      replace: true,
    });
  }

  const selectedDestRecords = selectedDest
    ? records.filter((r) => r.destinationDomain === selectedDest.domain)
    : [];
  const selectedScriptRecords = selectedScript
    ? records.filter((r) => (selectedScript.scriptId ? r.scriptId === selectedScript.scriptId : false))
    : [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link
            to="/crawls/$crawlId"
            params={{ crawlId }}
            className="text-xs text-muted-foreground hover:underline"
            data-testid="data-sharing-back-link"
          >
            ← Back to crawl
          </Link>
          <h1 className="text-xl font-semibold">Data sharing map</h1>
          <p className="text-xs text-muted-foreground">
            Who sends what data where. Left column: scripts. Right column: destinations. Edge colour = dominant
            payload category; thickness = payload count.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            className="h-8 w-40"
            value={consentMode}
            onChange={(e) => setSearch({ mode: e.target.value as ConsentMode | 'all' })}
            data-testid="data-sharing-mode-filter"
          >
            <option value="all">All modes</option>
            {CONSENT_MODES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </Select>
          <label className="flex items-center gap-1 text-xs">
            <input
              type="checkbox"
              checked={sensitiveOnly}
              onChange={(e) => setSearch({ sensitive: e.target.checked })}
              data-testid="data-sharing-sensitive-toggle"
            />
            Sensitive only
          </label>
        </div>
      </div>
      {total > records.length && (
        <div
          className="rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid="data-sharing-truncated"
        >
          Showing the first {records.length} of {total} payload records — map is truncated.
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        {payloadsQuery.isLoading ? (
          <div className="rounded-md border bg-background p-6 text-sm text-muted-foreground">Loading payloads…</div>
        ) : (
          <DataSharingMap
            graph={graph}
            onSelectDestination={(n) => {
              setSelectedDest(n);
              setSelectedScript(null);
            }}
            onSelectScript={(n) => {
              setSelectedScript(n);
              setSelectedDest(null);
            }}
          />
        )}
        <aside className="flex flex-col gap-3">
          {selectedDest && (
            <div className="rounded-md border bg-background p-3" data-testid="data-sharing-dest-panel">
              <div className="font-mono text-xs font-semibold">{selectedDest.domain}</div>
              <div className="text-[11px] text-muted-foreground">
                {selectedDestRecords.length} payload records
              </div>
              <ul className="mt-2 flex max-h-80 flex-col gap-1 overflow-auto text-xs">
                {selectedDestRecords.slice(0, 50).map((r) => (
                  <PayloadListItem key={r.id} record={r} />
                ))}
              </ul>
            </div>
          )}
          {selectedScript && (
            <div className="rounded-md border bg-background p-3" data-testid="data-sharing-script-panel">
              <div className="font-mono text-xs font-semibold">{selectedScript.label}</div>
              <div className="text-[11px] text-muted-foreground">
                {selectedScriptRecords.length} payload records
              </div>
              <ul className="mt-2 flex max-h-80 flex-col gap-1 overflow-auto text-xs">
                {selectedScriptRecords.slice(0, 50).map((r) => (
                  <PayloadListItem key={r.id} record={r} />
                ))}
              </ul>
            </div>
          )}
          {!selectedDest && !selectedScript && (
            <div className="rounded-md border bg-background p-3 text-xs text-muted-foreground">
              Click a node to see the payload records behind it.
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function PayloadListItem({ record }: { record: PayloadRecord }) {
  return (
    <li className="flex flex-col gap-0.5 rounded-sm border bg-muted/30 p-1.5">
      <div className="flex flex-wrap items-center gap-1">
        <span className="text-[10px] font-semibold text-muted-foreground">{record.method}</span>
        <span className="font-mono text-[10px]">{record.destinationDomain}</span>
        <span className="text-[10px] text-muted-foreground">· {record.consentMode}</span>
      </div>
      <div className="flex flex-wrap gap-1">
        {(record.payloadCategories ?? []).map((c) => (
          <PayloadCategoryChip key={c} category={c} />
        ))}
      </div>
    </li>
  );
}
